/**
 * Authentication hook and provider
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { User, UserRole } from '../types/user';

const TOKEN_KEY = 'myvault_access_token';
const REFRESH_TOKEN_KEY = 'myvault_refresh_token';

// Get stored token
function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// Store tokens
function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

// Clear tokens
function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// Simple fetch wrapper for auth endpoints
async function authFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

// API returns camelCase (FastAPI with Pydantic alias_generator)
interface UserApiResponse {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  avatarUrl?: string;
  status: string;
  roles: string[];
  subscriptionTier: string;
  mfaEnabled: boolean;
  emailVerified: boolean;
  timezone?: string;
  language?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  requires_2fa?: boolean;
  user?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    roles: string[];
    subscription_tier: string;
    mfa_enabled: boolean;
  };
}

function mapApiResponseToUser(data: UserApiResponse): User {
  const roles = data.roles || [];

  const role: UserRole = roles.includes('owner')
    ? 'owner'
    : roles.includes('admin')
      ? 'admin'
      : roles.includes('editor')
        ? 'editor'
        : 'viewer';

  const personas: User['personas'] = [];
  // BRD-019: Handle consolidated advisor role and legacy professional/intermediary
  if (roles.includes('advisor')) personas.push('advisor');
  if (roles.includes('professional')) personas.push('professional_accountant');
  if (roles.includes('intermediary')) personas.push('intermediary_advisor');
  // Only add tenant_admin if admin role but NOT owner (owners get platform_admin instead)
  if (roles.includes('admin') && !roles.includes('owner')) personas.push('tenant_admin');
  if (roles.includes('owner')) personas.push('platform_admin');

  return {
    id: data.id,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    displayName: data.fullName || `${data.firstName} ${data.lastName}`.trim(),
    avatarUrl: data.avatarUrl,
    tenantId: data.tenantId,
    role,
    personas,
    preferences: {
      locale: data.language || 'nl',
      timezone: data.timezone || 'Europe/Amsterdam',
      theme: 'system',
      notifications: {
        email: true,
        push: true,
        documentReminders: true,
        securityAlerts: true,
      },
    },
    subscriptionPlan: (data.subscriptionTier as User['subscriptionPlan']) || 'free',
    emailVerified: data.emailVerified,
    mfaEnabled: data.mfaEnabled,
    lastLoginAt: data.lastLoginAt,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export interface LoginResult {
  requires2FA?: boolean;
}

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean, otpCode?: string) => Promise<LoginResult | void>;
  logout: () => Promise<void>;
  /** Refresh user data from API. Returns true if successful, false if failed. */
  refreshUser: () => Promise<boolean>;
  /** For passwordless flows (email code, magic link) that return tokens from separate API calls */
  setTokensAndRefresh: (accessToken: string, refreshToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  onLoginRedirect?: () => void;
  onLogoutRedirect?: () => void;
}

export function AuthProvider({ children, onLoginRedirect, onLogoutRedirect }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      return false;
    }

    try {
      const response = await authFetch<UserApiResponse>('/api/v1/users/me');
      const userData = mapApiResponseToUser(response);
      setUser(userData);
      return true;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      clearTokens();
      setUser(null);
      return false;
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean, otpCode?: string): Promise<LoginResult | void> => {
      const data = await authFetch<LoginResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          remember_me: rememberMe,
          otp_code: otpCode,
        }),
      });

      if (data.requires_2fa) {
        return { requires2FA: true };
      }

      // Store tokens
      if (data.access_token && data.refresh_token) {
        storeTokens(data.access_token, data.refresh_token);
      }

      const success = await refreshUser();
      if (success) {
        onLoginRedirect?.();
      }
    },
    [refreshUser, onLoginRedirect]
  );

  const logout = useCallback(async () => {
    try {
      await authFetch('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // Ignore logout errors
    }
    clearTokens();
    setUser(null);
    onLogoutRedirect?.();
  }, [onLogoutRedirect]);

  // For passwordless flows (email code, magic link) that return tokens from separate API calls
  const setTokensAndRefresh = useCallback(
    async (accessToken: string, refreshToken: string): Promise<void> => {
      storeTokens(accessToken, refreshToken);
      const success = await refreshUser();
      if (success) {
        onLoginRedirect?.();
      } else {
        // refreshUser failed - throw so caller can handle the error
        throw new Error('Failed to authenticate with the provided tokens');
      }
    },
    [refreshUser, onLoginRedirect]
  );

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await refreshUser();
      setIsLoading(false);
    };
    initAuth();
  }, [refreshUser]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    setTokensAndRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
